import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { COOKIE_NOTICE_PRIVACY_POLICY_URL, PRIVACY_POLICY_VERSION } from '@/data/privacyPolicy';
import useConsentContext from '@/contexts/Consent/useConsentContext';
import styles from './PrivacyPolicyPage.module.scss';

/**
 * מדיניות הפרטיות של האזור האישי (HAT-1081).
 *
 * העמוד יושב באפליקציה עצמה ולא באתר התדמית, כי הוא חייב להיות זמין בכל
 * ה-tenants: bro2bro ו-hatzdakaUS הם ארגונים אחרים בדומיינים אחרים, ומדיניות
 * של מרכז הצדקה אינה חלה עליהם. הוא מתאר את מה שהמערכת הזו עושה - שלושת כלי
 * המדידה, הקלטות הסשן והמזהה הפסאודונימי - ומפנה למדיניות המלאה של הארגון
 * כשזו מוגדרת ל-tenant.
 *
 * נתיב ציבורי בכוונה: גם מבקר שאינו מחובר (עמודי תרומה, פתיחת קרן) והבאנר
 * עצמו חייבים להגיע אליו.
 */
const PrivacyPolicyPage = () => {
  const { t } = useTranslation();
  const { openNotice } = useConsentContext();

  useEffect(() => {
    document.title = t('privacyPolicy.title');
  }, [t]);

  const siteName = t(`siteTitle.${import.meta.env.VITE_APP_SITE_ID}`, { defaultValue: '' })
    .split('|')
    .pop()
    ?.trim();

  return (
    <main className={styles.page}>
      <h1 className="heading-h2-bold">{t('privacyPolicy.title')}</h1>
      <p className={styles.meta}>
        {t('privacyPolicy.version')} <span dir="ltr">{PRIVACY_POLICY_VERSION}</span>
        {siteName ? ` · ${siteName}` : ''}
      </p>

      <section>
        <p>{t('privacyPolicy.intro')}</p>
      </section>

      <section>
        <h2 className="heading-h3-semibold">{t('privacyPolicy.dataTitle')}</h2>
        <p>{t('privacyPolicy.dataAccount')}</p>
        <p>{t('privacyPolicy.dataFund')}</p>
      </section>

      <section>
        <h2 className="heading-h3-semibold">{t('privacyPolicy.toolsTitle')}</h2>
        <p>{t('privacyPolicy.toolsIntro')}</p>
        <ul className={styles.list}>
          <li>{t('privacyPolicy.toolGa')}</li>
          <li>{t('privacyPolicy.toolPostHog')}</li>
          <li>{t('privacyPolicy.toolAppInsights')}</li>
        </ul>
        <p>{t('privacyPolicy.toolsConsent')}</p>
      </section>

      <section>
        <h2 className="heading-h3-semibold">{t('privacyPolicy.pseudonymTitle')}</h2>
        <p>{t('privacyPolicy.pseudonymBody')}</p>
      </section>

      <section>
        <h2 className="heading-h3-semibold">{t('privacyPolicy.recordingsTitle')}</h2>
        <p>{t('privacyPolicy.recordingsBody')}</p>
      </section>

      <section>
        <h2 className="heading-h3-semibold">{t('privacyPolicy.storageTitle')}</h2>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('privacyPolicy.storageColGroup')}</th>
                <th>{t('privacyPolicy.storageColKeys')}</th>
                <th>{t('privacyPolicy.storageColPurpose')}</th>
                <th>{t('privacyPolicy.storageColConsent')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('privacyPolicy.storageNecessary')}</td>
                <td dir="ltr">hatzdaka.cookie-consent, currentUser</td>
                <td>{t('privacyPolicy.storageNecessaryPurpose')}</td>
                <td>{t('privacyPolicy.storageNoConsent')}</td>
              </tr>
              <tr>
                <td>Google Analytics</td>
                <td dir="ltr">_ga, _ga_*, _gid, _gat*</td>
                <td>{t('privacyPolicy.storageGaPurpose')}</td>
                <td>{t('privacyPolicy.storageYesConsent')}</td>
              </tr>
              <tr>
                <td>PostHog</td>
                <td dir="ltr">ph_*</td>
                <td>{t('privacyPolicy.storagePostHogPurpose')}</td>
                <td>{t('privacyPolicy.storageYesConsent')}</td>
              </tr>
              <tr>
                <td>Application Insights</td>
                <td dir="ltr">ai_user, ai_session</td>
                <td>{t('privacyPolicy.storageAppInsightsPurpose')}</td>
                <td>{t('privacyPolicy.storageYesConsent')}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>{t('privacyPolicy.storageSweep')}</p>
        <button type="button" className={styles.settingsButton} onClick={openNotice}>
          {t('privacyPolicy.changeSettings')}
        </button>
      </section>

      <section>
        <h2 className="heading-h3-semibold">{t('privacyPolicy.rightsTitle')}</h2>
        <p>{t('privacyPolicy.rightsBody')}</p>
      </section>

      <section>
        <h2 className="heading-h3-semibold">{t('privacyPolicy.securityTitle')}</h2>
        <p>{t('privacyPolicy.securityBody')}</p>
      </section>

      {/* מוצג רק ל-tenant שיש לו עמוד מדיניות ארגוני - שם נמצאים גם פרטי הקשר. */}
      {COOKIE_NOTICE_PRIVACY_POLICY_URL && (
        <section>
          <h2 className="heading-h3-semibold">{t('privacyPolicy.fullPolicyTitle')}</h2>
          <p>
            {t('privacyPolicy.fullPolicyBody')}{' '}
            <a href={COOKIE_NOTICE_PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" className={styles.link}>
              {t('privacyPolicy.fullPolicyLink')}
            </a>
            .
          </p>
        </section>
      )}
    </main>
  );
};

export default PrivacyPolicyPage;
